import { SCHLUESSEL_STANDARD, effektiverRaumName } from './katalog';
import { neuePartei, ergaenzeKennungen } from './unterschriften';
import type { Partei, Protokoll, RaumBlock } from './typen';
import type { Bezug } from './bezug';

// ─────────────────────────────────────────────────────────────────────────────
// Notanker (Fix-Runde 1+2, Review nach Task 8/9)
//
// Der 500-ms-Debounce in UebergabeAssistent.tsx reicht nicht, wenn die Seite
// *sofort* verschwindet (F5, Pull-to-Refresh, Tab schließen, App vom Betriebssystem
// beendet) — pagehide/visibilitychange laufen dann zwar, aber ein put() in
// IndexedDB ist async und wurde im Praxistest (echter reload() ~150 ms nach der
// letzten Eingabe) vom Browser abgebrochen, bevor die Transaktion committen konnte.
// localStorage.setItem ist dagegen synchron und übersteht das zuverlässig (ebenfalls
// geprüft) — dient hier als Notanker, der beim nächsten Laden in die echte
// IndexedDB übernommen wird. Enthält nur `protokoll` und die Unterschrift-
// Entwurfsfelder (beide JSON-fähig), keine Fotos/Unterschriften-Blobs.
//
// Weil der Notanker keine Blobs tragen kann, kann sein Protokoll-Stand auf einen
// Foto-/Unterschrift-Pfad verweisen, dessen Blob nur in einer ÄLTEREN IndexedDB-
// Kopie existiert (z. B. Absturz unmittelbar nach einem Mangel-Foto). Beim
// Übernehmen werden deshalb alle Pfade ohne zugehörigen Blob entfernt und als
// Verlust gemeldet — sonst würde das Protokoll Vollständigkeit vortäuschen.
// ─────────────────────────────────────────────────────────────────────────────

/** Abstraktion über den Schlüssel/Wert-Speicher des Notankers — injizierbar für Tests (Muster wie `Speicher` oben). */
export interface NotfallSpeicher {
  liesRoh(schluessel: string): string | null;
  schreibeRoh(schluessel: string, wert: string): void;
  entferneRoh(schluessel: string): void;
}

function browserLocalStorage(): NotfallSpeicher {
  return {
    liesRoh: (k) => localStorage.getItem(k),
    schreibeRoh: (k, v) => localStorage.setItem(k, v),
    entferneRoh: (k) => localStorage.removeItem(k),
  };
}

let standardNotfall: NotfallSpeicher | null = null;
function standardNotfallSpeicher(): NotfallSpeicher {
  if (!standardNotfall) standardNotfall = browserLocalStorage();
  return standardNotfall;
}

export function notfallSchluessel(id: string): string {
  return `uebergabe-notfall:${id}`;
}

export interface NotfallSchnappschuss {
  protokoll: Protokoll;
  unterschrift_entwuerfe?: LokalerEntwurf['unterschrift_entwuerfe'];
  gespeichert_am: string;
}

export interface NotfallUebernahmeErgebnis {
  entwurf: LokalerEntwurf | null;
  /** Menschenlesbare Fundstellen entfernter Foto-/Unterschrift-Verweise, z. B. "Bad: Sanitär". */
  fehlendeMedien: string[];
}

const ROLLEN_LABEL: Record<Partei['rolle'], string> = {
  uebergeber: 'Übergeber',
  uebernehmer: 'Übernehmer',
  zeuge: 'Zeuge',
};

/**
 * Entfernt aus einem Protokoll alle Foto-/Unterschrift-Pfade, zu denen kein Blob
 * vorliegt (Raum-Fotos, Mängel-Fotos, Zähler-Fotos, Unterschriften-PNGs), und meldet sie.
 * Ein Raum/Prüfpunkt/Zähler bleibt erhalten, verliert nur den fehlenden Foto-Verweis;
 * eine Unterschrift ohne PNG wird komplett entfernt (die Partei gilt wieder als
 * nicht unterschrieben — die einzige ehrliche Option, `png_path` ist nicht optional).
 */
export function bereinigeFehlendeMedien(
  protokoll: Protokoll,
  fotos: Record<string, Blob>,
  unterschriften: Record<string, Blob>,
): { protokoll: Protokoll; fehlendeMedien: string[] } {
  const fehlendeMedien: string[] = [];

  const raeume = protokoll.raeume.map(raum => {
    const raumName = effektiverRaumName(raum);
    // Raum-Fotos (Praxis-Ergänzung: Zustandsdokumentation unabhängig von Mängeln) laufen
    // durch dieselbe Bereinigung wie Prüfpunkt-Fotos — sonst bliebe nach einem Absturz
    // eine tote Bildreferenz auf dem Raumblock stehen.
    const raumFotos = (raum.fotos ?? []).filter(pfad => fotos[pfad]);
    if (raumFotos.length !== (raum.fotos ?? []).length) fehlendeMedien.push(`${raumName}: Raumfoto`);
    return {
      ...raum,
      fotos: raumFotos,
      // `?? []`: ein Raumblock aus einem NOCH älteren Entwurf könnte (theoretisch) auch
      // `pruefpunkte` selbst vermissen lassen — dieselbe Vorsicht wie überall sonst in
      // diesem Modul, s. vervollstaendigeEntwurf() weiter unten.
      pruefpunkte: (raum.pruefpunkte ?? []).map(pp => {
        const behalten = (pp.fotos ?? []).filter(pfad => fotos[pfad]);
        if (behalten.length === (pp.fotos ?? []).length) return pp;
        fehlendeMedien.push(`${raumName}: ${pp.name}`);
        return { ...pp, fotos: behalten };
      }),
    };
  });

  const zaehler = protokoll.zaehler.map(z => {
    if (!z.foto || fotos[z.foto]) return z;
    fehlendeMedien.push(`Zähler ${z.art} · ${z.nummer}`);
    return { ...z, foto: null };
  });

  const unterschriftenListe = protokoll.unterschriften.filter(u => {
    if (unterschriften[u.png_path]) return true;
    fehlendeMedien.push(`Unterschrift ${ROLLEN_LABEL[u.rolle] ?? u.rolle}`);
    return false;
  });

  return { protokoll: { ...protokoll, raeume, zaehler, unterschriften: unterschriftenListe }, fehlendeMedien };
}

/** Schreibt den Notanker synchron — Fehler (z. B. voller/deaktivierter Speicher) dürfen nicht crashen. */
export function schreibeNotfallSchnappschuss(entwurf: LokalerEntwurf, speicher: NotfallSpeicher = standardNotfallSpeicher()): void {
  try {
    const schnappschuss: NotfallSchnappschuss = {
      protokoll: entwurf.protokoll,
      unterschrift_entwuerfe: entwurf.unterschrift_entwuerfe,
      gespeichert_am: new Date().toISOString(),
    };
    speicher.schreibeRoh(notfallSchluessel(entwurf.id), JSON.stringify(schnappschuss));
  } catch {
    // z. B. localStorage voll oder deaktiviert (privater Modus) — dann trägt nur
    // noch der reguläre Debounce-Pfad, der bei einem harten Reload ohnehin riskiert war.
  }
}

/**
 * Übernimmt einen Notanker, falls vorhanden und neuer als der IndexedDB-Stand,
 * bereinigt dabei Foto-/Unterschrift-Pfade ohne Blob und meldet sie. Der Schlüssel
 * wird in jedem Fall entfernt, sobald etwas gelesen wurde (auch bei kaputtem JSON) —
 * sonst bliebe er bei einem beschädigten Eintrag für immer liegen.
 */
export function uebernehmeNotfallSchnappschuss(
  protokollId: string,
  vorhanden: LokalerEntwurf | null,
  speicher: NotfallSpeicher = standardNotfallSpeicher(),
): NotfallUebernahmeErgebnis {
  let ergebnis = vorhanden;
  let fehlendeMedien: string[] = [];
  let roh: string | null = null;
  try {
    roh = speicher.liesRoh(notfallSchluessel(protokollId));
  } catch {
    roh = null; // localStorage nicht verfügbar — dann zählt nur der IndexedDB-Stand.
  }
  if (roh) {
    try {
      const schnappschuss = JSON.parse(roh) as NotfallSchnappschuss;
      if (!ergebnis || schnappschuss.gespeichert_am > ergebnis.gespeichert_am) {
        const bereinigt = bereinigeFehlendeMedien(schnappschuss.protokoll, vorhanden?.fotos ?? {}, vorhanden?.unterschriften ?? {});
        fehlendeMedien = bereinigt.fehlendeMedien;
        // IMPORTANT 3 (Abschluss-Review): Kennungen werden sonst NUR über ladeEntwurf()
        // nachgerüstet (vervollstaendigeEntwurf -> ergaenzeKennungen). Der Notanker ersetzt
        // `protokoll` hier aber vollständig und wird danach zurückgespeichert (s.
        // UebergabeAssistent.tsx) — ohne diesen Aufruf gewönne beim Rollout (App geht
        // während des Deploys in den Hintergrund, Notanker greift beim nächsten Start) der
        // kennungslose Stand der alten Version. Danach fällt gehoertZu() auf die Rolle
        // zurück, eine neue Zeugenkarte zeigt die Unterschrift des ERSTEN Zeugen, und beim
        // Signieren des neuen Zeugen wird dessen Unterschrift still verworfen.
        const { parteien, unterschriften } = ergaenzeKennungen(bereinigt.protokoll);
        const protokoll: Protokoll = { ...bereinigt.protokoll, parteien, unterschriften };
        ergebnis = ergebnis
          ? {
              ...ergebnis,
              protokoll,
              unterschrift_entwuerfe: schnappschuss.unterschrift_entwuerfe ?? ergebnis.unterschrift_entwuerfe,
              gespeichert_am: schnappschuss.gespeichert_am,
            }
          : {
              id: protokollId,
              protokoll,
              fotos: {},
              unterschriften: {},
              unterschrift_entwuerfe: schnappschuss.unterschrift_entwuerfe,
              gespeichert_am: schnappschuss.gespeichert_am,
            };
      }
    } catch {
      // kaputtes JSON — Notanker verwerfen, IndexedDB-Stand bleibt gültig.
    } finally {
      try { speicher.entferneRoh(notfallSchluessel(protokollId)); } catch { /* localStorage nicht verfügbar — nichts zu tun */ }
    }
  }
  return { entwurf: ergebnis, fehlendeMedien };
}
// ─────────────────────────────────────────────────────────────────────────────

// Editierbarer Vorab-Stand einer Unterschriften-Karte (Schritt 6), bevor tatsächlich
// signiert wurde. Bewusst KEIN Feld von `Protokoll`: `lib/uebergabe/abschluss.ts`
// upsertet ausschließlich `entwurf.protokoll` nach Postgres, ein Feld hier würde also
// nie versehentlich in die Datenbank gelangen (und keine Migration erfordern).
export interface UnterschriftEntwurf {
  name: string;
  ort: string;
  datum: string;
}

export interface LokalerEntwurf {
  id: string;
  protokoll: Protokoll;
  fotos: Record<string, Blob>;           // Storage-Pfad -> Blob (noch nicht hochgeladen)
  unterschriften: Record<string, Blob>;  // Storage-Pfad -> PNG-Blob
  // Je PARTEI (Schlüssel: `pid`, nicht mehr die Rolle) Name/Ort/Datum, solange noch nicht
  // unterschrieben wurde — überlebt dadurch Schrittwechsel und Reload wie jedes andere
  // Entwurfsfeld. Schlüssel auf `pid` umgestellt (Task-2-Brief): mit mehreren Zeugen
  // teilten sich sonst alle Zeugen dasselbe Ort-/Datumsfeld, weil die Rolle sie nicht
  // mehr eindeutig identifiziert. Optional, damit ältere, bereits lokal gespeicherte
  // Entwürfe (ohne dieses Feld) weiter ladbar sind.
  unterschrift_entwuerfe?: Partial<Record<string, UnterschriftEntwurf>>;
  gespeichert_am: string;
  // ── Geräteübergreifender Abgleich (Ticket #488, s. sync.ts) — alle optional, damit
  // Entwürfe von vor dem Abgleich weiter ladbar sind. ──
  /** `updated_at` der Server-Zeile, auf dem dieses Gerät zuletzt war. */
  server_stand?: string | null;
  /** Lokale Änderungen, die noch nicht auf dem Server sind. */
  ungesichert?: boolean;
  /** Mediendateien, die schon im Bucket liegen — werden nicht erneut hochgeladen. */
  hochgeladen?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Neuanlage eines Entwurfs (Fix-Runde 1: aus UebergabeAssistent.tsx hierher
// verschoben) — jetzt von ZWEI Stellen gebraucht: eager beim Bestätigen der
// Pillen-Vorauswahl (UebergabeListe.tsx.starteUebergabe, damit ein schneller Reload
// direkt danach nichts verliert) und weiterhin als defensiver Fallback im Assistenten
// selbst (falls die eager-Anlage dort z. B. an blockiertem IndexedDB scheiterte).
//
// Ohne bezug (Direktlink/Reload ohne bekannten Bezug) verhält sich das wie vorher: Art
// "einzug", nichts vorbelegt. Mit bezug sind Art/Objekt/Einheit/Kontakt-IDs bereits
// gesetzt; parteienVorbelegt kommt fertig aus verteileRollen (s. lib/uebergabe/bezug.ts)
// — ohne sie (z. B. weil das Laden der Kontakte fehlschlug) bleiben die Parteien leer,
// nur Objekt/Einheit sind vorbelegt.
//
// Startet ohne Zeugen (Task-2-Brief, Entscheidung des Auftraggebers): das leere
// Zeugenfeld wirkte wie eine unerledigte Pflicht, obwohl der Zeuge freiwillig ist. Ein
// Zeuge wird bei Bedarf im Assistenten über fuegeZeugenHinzu() angelegt. Übergeber/
// Übernehmer entstehen über neuePartei(), damit sie von Anfang an eine `pid` tragen.
export function erzeugeEntwurf(
  protokollId: string,
  userProfile: { id?: string | null; full_name?: string | null } | null | undefined,
  bezug?: Bezug | null,
  parteienVorbelegt?: Partei[],
): LokalerEntwurf {
  const jetzt = new Date().toISOString();
  const protokoll: Protokoll = {
    id: protokollId,
    art: bezug?.art ?? 'einzug',
    status: 'in_erfassung',
    objekt_id: bezug?.objekt_id ?? null,
    einheit_id: bezug?.einheit_id ?? null,
    mieter_id: bezug?.mieter_id ?? null,
    eigentuemer_id: bezug?.eigentuemer_id ?? null,
    vertrag_id: null,
    vorgaenger_id: null,
    durchgefuehrt_am: jetzt,
    erfasst_von: userProfile?.id ?? null,
    parteien: parteienVorbelegt ?? [neuePartei('uebergeber'), neuePartei('uebernehmer')],
    raeume: [],
    zaehler: [],
    schluessel: SCHLUESSEL_STANDARD.map(z => ({ ...z })),
    bemerkungen: null,
    renoviert_am: null,
    nebenkosten_bezahlt: null,
    nebenkosten_notiz: null,
    unterschriften: [],
    pdf_path: null,
    pdf_hash: null,
    drive_file_id: null,
    mail_versendet_am: null,
    created_at: jetzt,
    updated_at: jetzt,
  };
  return { id: protokollId, protokoll, fotos: {}, unterschriften: {}, unterschrift_entwuerfe: {}, gespeichert_am: jetzt };
}
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Vervollständigung beim NORMALEN Laden (Review nach dem Tablet-Live-Absturz „Raumfoto
// bei altem Entwurf"):
//
// RaumBlock hat seit der Erstanlage dieses Moduls bereits neue Pflicht-Listenfelder
// dazubekommen (`fotos`, `zaehler_ids` s. Kommentar an RaumBlock in typen.ts) — ein
// Entwurf, der lokal auf einem Tablet VOR so einer Änderung begonnen wurde, trägt das
// betroffene Feld im in IndexedDB gespeicherten JSON schlicht nicht. TypeScript fängt das
// nicht (das Feld ist im Typ Pflicht, die Lücke besteht nur zwischen Typ und tatsächlich
// gespeicherten Altdaten) — ungeschützt iteriert (Spread, `.map`, `.filter`, `.includes`
// in SchrittRundgang.tsx/RaumKarte.tsx) stürzt das sofort ab, sobald der Nutzer den
// betroffenen Raum berührt. Einzelne Komponenten wurden zusätzlich defensiv gemacht
// (`?? []` an jeder Zugriffsstelle) — das hier ist die zweite, zentrale Absicherung:
// jeder über ladeEntwurf() geladene Entwurf bekommt fehlende Listenfelder EINMAL beim
// Laden als leere Liste ergänzt, damit dieselbe Fehlerklasse beim nächsten neuen
// Listenfeld nicht wiederkommt, ohne dass jede Komponente sie erneut selbst abfangen
// muss.
//
// Bewusst NUR im normalen Ladepfad (ladeEntwurf), nicht im Notanker-Wiederherstellungspfad
// (uebernehmeNotfallSchnappschuss): der hat mit bereinigeFehlendeMedien() bereits eine
// eigene, andere Aufgabe (verwaiste Foto-/Unterschrift-Pfade entfernen) und liefert für
// `raeume` ohnehin schon einen vollständigen Stand (setzt `fotos`/`pruefpunkte` je Raum
// explizit, s. oben).
//
// Ergänzt NUR fehlende Felder (`??`, nicht `||`) — eine bereits vorhandene, aber LEERE
// Liste bleibt unangetastet, genau wie jeder andere vorhandene Wert.
/** Vervollständigt fehlende Listenfelder eines Raumblocks — s. Kommentarblock oben. */
function vervollstaendigeRaumBlock(raum: RaumBlock): RaumBlock {
  return {
    ...raum,
    fotos: raum.fotos ?? [],
    zaehler_ids: raum.zaehler_ids ?? [],
    pruefpunkte: (raum.pruefpunkte ?? []).map(pp => ({ ...pp, fotos: pp.fotos ?? [] })),
  };
}

export function vervollstaendigeEntwurf(entwurf: LokalerEntwurf): LokalerEntwurf {
  // ergaenzeKennungen(): dieselbe Vervollständigungs-Idee wie bei den Raumblock-Listen
  // oben, nur für Partei-/Unterschrift-Kennungen — ein lokal begonnener Entwurf von vor
  // dieser Version kennt `pid` noch nicht (s. Kommentar dort).
  const { parteien, unterschriften } = ergaenzeKennungen(entwurf.protokoll);
  return {
    ...entwurf,
    protokoll: {
      ...entwurf.protokoll,
      raeume: (entwurf.protokoll.raeume ?? []).map(vervollstaendigeRaumBlock),
      parteien,
      unterschriften,
    },
  };
}
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Entfernt einen Zeugen restlos: Partei, seine Unterschrift, den PNG-Blob und den
 * Feldentwurf (Name/Ort/Datum). Ohne das Aufräumen von Blob und Feldentwurf bliebe eine
 * verwaiste PNG im Entwurf liegen und würde beim Abschluss mit hochgeladen — der Blob
 * lebt nur hier im lokalen Entwurf, nicht in `entwurf.protokoll`, deshalb steht diese
 * Funktion in lokal.ts statt bei den übrigen Partei-Helfern in unterschriften.ts.
 *
 * Nur Zeugen sind entfernbar (Übergeber/Übernehmer sind Pflichtparteien, s.
 * PFLICHT_ROLLEN in unterschriften.ts) — bei jeder anderen Rolle oder einer unbekannten
 * `pid` bleibt der Entwurf unverändert.
 *
 * IMPORTANT 3 (Abschluss-Review, harter Riegel): Ohne gültige `pid` passiert NICHTS.
 * `entwurf.protokoll.parteien.find(p => p.pid === pid)` liefert bei `pid === undefined`
 * sonst die erste Partei OHNE Kennung — trifft die ein Zeuge, ist das harmlos, trifft sie
 * (bei einem noch nicht per ergaenzeKennungen nachgerüsteten Entwurf) Übergeber oder
 * Übernehmer, filterte der bisherige Code sie über `parteien.filter(p => p.pid !== pid)`
 * gleich mit heraus — genau die Zusicherung „Übergeber und Übernehmer sind nicht
 * entfernbar" wäre damit verletzt. Der Aufrufer (SchrittUnterschriften.tsx) reicht
 * `partei.pid` durch, das bei Altdaten fehlen kann.
 */
export function entferneZeugen(entwurf: LokalerEntwurf, pid: string | undefined): LokalerEntwurf {
  if (!pid) return entwurf;
  const partei = entwurf.protokoll.parteien.find(p => p.pid === pid);
  if (!partei || partei.rolle !== 'zeuge') return entwurf;

  const sig = entwurf.protokoll.unterschriften.find(u => u.pid === pid);
  const unterschriften = { ...entwurf.unterschriften };
  if (sig) delete unterschriften[sig.png_path];

  const entwuerfe = { ...(entwurf.unterschrift_entwuerfe ?? {}) };
  delete entwuerfe[pid];

  return {
    ...entwurf,
    protokoll: {
      ...entwurf.protokoll,
      parteien: entwurf.protokoll.parteien.filter(p => p.pid !== pid),
      unterschriften: entwurf.protokoll.unterschriften.filter(u => u.pid !== pid),
    },
    unterschriften,
    unterschrift_entwuerfe: entwuerfe,
  };
}

export interface Speicher {
  hole(id: string): Promise<LokalerEntwurf | null>;
  setze(entwurf: LokalerEntwurf): Promise<void>;
  entferne(id: string): Promise<void>;
  alle(): Promise<LokalerEntwurf[]>;
}

const DB_NAME = 'immo-uebergabe';
const STORE = 'entwuerfe';

export function erzeugeIndexedDbSpeicher(): Speicher {
  // Verbindung wird einmal geöffnet und danach wiederverwendet — sonst sammeln sich
  // während einer Übergabe (jede Eingabe, jedes Foto löst eine Speicherung aus) offene
  // Verbindungen an.
  let verbindung: Promise<IDBDatabase> | null = null;

  function oeffne(): Promise<IDBDatabase> {
    if (!verbindung) {
      verbindung = new Promise<IDBDatabase>((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
          if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' });
        };
        req.onsuccess = () => {
          const db = req.result;
          // Ein späterer Versionswechsel (z. B. durch einen anderen Tab) darf nicht an
          // dieser Verbindung hängen bleiben: schließen und Cache zurücksetzen, damit der
          // nächste Aufruf neu öffnet.
          db.onversionchange = () => {
            db.close();
            verbindung = null;
          };
          resolve(db);
        };
        req.onerror = () => {
          verbindung = null;
          reject(req.error);
        };
        req.onblocked = () => {
          verbindung = null;
          reject(new Error('IndexedDB-Öffnung blockiert: eine andere Verbindung verhindert den Versionswechsel.'));
        };
      });
    }
    return verbindung;
  }

  // Führt eine einzelne Anfrage in einer eigenen Transaktion aus. Aufgelöst wird erst
  // bei tx.oncomplete: request.onsuccess bedeutet nur, dass die Anfrage in die
  // Transaktion aufgenommen wurde, nicht dass sie committet ist (bricht die Transaktion
  // danach ab, z. B. QuotaExceededError bei großen Foto-/Unterschrift-Blobs, wäre sonst
  // fälschlich „gespeichert" gemeldet worden). Bei Lesevorgängen wird der Wert aus
  // request.onsuccess zwischengespeichert und erst beim Transaktionsabschluss
  // zurückgegeben, damit Ergebnisübergabe und Abschluss sauber zusammenlaufen (kein
  // doppeltes Auflösen desselben Promise).
  function transaktion<T>(
    modus: IDBTransactionMode,
    ausfuehren: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    return oeffne().then(
      db =>
        new Promise<T>((resolve, reject) => {
          const tx = db.transaction(STORE, modus);
          const req = ausfuehren(tx.objectStore(STORE));
          let ergebnis: T;
          req.onsuccess = () => {
            ergebnis = req.result;
          };
          req.onerror = () => reject(req.error);
          tx.oncomplete = () => resolve(ergebnis);
          tx.onerror = () => reject(tx.error);
          tx.onabort = () => reject(tx.error);
        })
    );
  }

  return {
    async hole(id) {
      const ergebnis = await transaktion<LokalerEntwurf | undefined>('readonly', store => store.get(id));
      return ergebnis ?? null;
    },
    async setze(entwurf) {
      await transaktion('readwrite', store => store.put(entwurf));
    },
    async entferne(id) {
      await transaktion('readwrite', store => store.delete(id));
    },
    async alle() {
      const ergebnis = await transaktion<LokalerEntwurf[]>('readonly', store => store.getAll());
      return ergebnis ?? [];
    },
  };
}

let standard: Speicher | null = null;
function standardSpeicher(): Speicher {
  if (!standard) standard = erzeugeIndexedDbSpeicher();
  return standard;
}

export async function speichereEntwurf(entwurf: LokalerEntwurf, s: Speicher = standardSpeicher()): Promise<void> {
  await s.setze({ ...entwurf, gespeichert_am: new Date().toISOString() });
}

export async function ladeEntwurf(id: string, s: Speicher = standardSpeicher()): Promise<LokalerEntwurf | null> {
  const entwurf = await s.hole(id);
  // vervollstaendigeEntwurf(): s. Kommentarblock oben — ergänzt fehlende Listenfelder an
  // Altdatensätzen, bevor der Entwurf in den Assistenten (UebergabeAssistent.tsx) gelangt.
  return entwurf ? vervollstaendigeEntwurf(entwurf) : null;
}

// Entfernt den Entwurf UND einen eventuell noch liegenden Notanker in einem Zug —
// hier statt an jeder Aufrufstelle (erfolgreicher Abschluss, künftig auch
// „Verwerfen"), damit keine davon es vergessen kann. Ein Notanker bleibt sonst
// unbegrenzt liegen, sobald während der Erfassung auch nur einmal ein Tab-Wechsel
// einen Flush ausgelöst hat — jede Übergabe bekommt eine neue Kennung, die alten
// Schlüssel würden sich in localStorage ansammeln.
export async function loescheEntwurf(
  id: string,
  s: Speicher = standardSpeicher(),
  notfall: NotfallSpeicher = standardNotfallSpeicher(),
): Promise<void> {
  await s.entferne(id);
  try {
    notfall.entferneRoh(notfallSchluessel(id));
  } catch {
    // localStorage nicht verfügbar — der eigentliche Entwurf ist trotzdem weg.
  }
}

export async function listeOffene(s: Speicher = standardSpeicher()): Promise<LokalerEntwurf[]> {
  const alle = await s.alle();
  return [...alle].sort((a, b) => b.gespeichert_am.localeCompare(a.gespeichert_am));
}
